import { type MouseEvent, useLayoutEffect, useRef, useState } from "react";
import CSSTransition from "react-transition-group/CSSTransition";
import { shareTypes } from "@/modules/shareUtils";
import { eventNames } from "@/modules/Constants";

import EmailIcon from "@svg/email.svg";
import FacebookIcon from "@svg/facebook.svg";
import ShareIcon from "@svg/share.svg";
import TwitterIcon from "@svg/twitter.svg";

const REGION = "Share";

type ShareProps = {
  link?: unknown;
  onApplicationEvent?: (event: Record<string, unknown>) => void;
};

export default function Share({ link, onApplicationEvent }: ShareProps) {
  const [active, setActive] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // The committed `active`, read by the window listener as `this.state.active`.
  const activeRef = useRef(active);

  // Declared first: the window listener reads the state just committed.
  useLayoutEffect(() => {
    activeRef.current = active;
  });

  // componentDidMount / componentWillUnmount (layout phase, as the class ran).
  useLayoutEffect(() => {
    function onWindowClick(e: Event) {
      if (!e.defaultPrevented && activeRef.current) {
        setActive(false);
      }
    }
    // Escape closes the open menu and returns focus to the share button.
    function onWindowKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && activeRef.current) {
        setActive(false);
        buttonRef.current?.focus();
      }
    }
    window.addEventListener("click", onWindowClick);
    window.addEventListener("keydown", onWindowKeyDown);
    return () => {
      window.removeEventListener("click", onWindowClick);
      window.removeEventListener("keydown", onWindowKeyDown);
    };
  }, []);

  function onMenuClick(e: MouseEvent<HTMLButtonElement>) {
    if (!e.defaultPrevented) {
      setActive(!active);
      e.preventDefault();
    }
  }

  function onMenuItemClick(e: MouseEvent<HTMLButtonElement>) {
    if (!e.defaultPrevented && onApplicationEvent && link) {
      const type = (e.target as Element).getAttribute("data-type");
      onApplicationEvent({
        region: REGION,
        name: eventNames.SHARE_LINK,
        linkType: type,
        link,
      });
    }
    setActive(false);
    e.preventDefault();
  }

  return (
    <div className="share-btn button type-4 hover wide tw:relative">
      <ShareIcon className="centered icon-share-d" />
      {/* Covers the icon, which stays a direct child for the `.button.type-4 > svg` brand colors */}
      <button
        ref={buttonRef}
        type="button"
        className="tw:absolute tw:inset-0"
        aria-label="Share"
        aria-expanded={active}
        onClick={onMenuClick}
      />
      <CSSTransition
        nodeRef={menuRef}
        classNames="share-menu"
        in={active}
        mountOnEnter={true}
        unmountOnExit={true}
        timeout={200}
      >
        <div
          className="share-menu tw:absolute tw:flex tw:flex-col tw:bg-[#000328] tw:[transition:transform_0.2s_cubic-bezier(0,0,0,1),opacity_0.2s] tw:[.landscape_&]:top-full tw:[.landscape_&]:left-[10px] tw:[.portrait_&]:bottom-full tw:[.portrait_&]:left-[10px]"
          ref={menuRef}
        >
          <button
            className="button hover square tw:relative"
            data-type={shareTypes.EMAIL}
            aria-label="Share by email"
            onClick={onMenuItemClick}
          >
            <EmailIcon className="centered icon-email-d" />
          </button>
          <button
            className="button hover square tw:relative"
            data-type={shareTypes.TWITTER}
            aria-label="Share on Twitter"
            onClick={onMenuItemClick}
          >
            <TwitterIcon className="centered icon-twitter-d" />
          </button>
          <button
            className="button hover square tw:relative"
            data-type={shareTypes.FACEBOOK}
            aria-label="Share on Facebook"
            onClick={onMenuItemClick}
          >
            <FacebookIcon className="centered icon-facebook-d" />
          </button>
        </div>
      </CSSTransition>
    </div>
  );
}
